import PortalDocument, {PortalDocumentInterface} from "../../PortalDocument";
import React, {createRef, ReactNode, RefObject} from "react";
import {Button} from "primereact/button";
import {Tree, TreeEventNodeEvent, TreeExpandedKeysType} from "primereact/tree";
import {InputText} from "primereact/inputtext";
import {Menu} from "primereact/menu";
import SortHelper from "../../../../shared/helper/SortHelper";
import Observer from "../../../../shared/class/Observer";
import LocationEvent from "../../../viewer/LocationEvent";
import {I18nGlobals} from "../../../../shared/translation/i18nInterface";
import DocumentSettings from "../../DocumentSettings";
import {
	Annotation,
	AnnotationPoint,
	AnnotationPointPoint,
	AnnotationRectangle,
	AnnotationRectangleRectangle,
	AnnotationsDocument,
	BaseToolbox,
	BorderStyle,
	BorderStyle as BorderStyleEnum,
	CircleAnnotation,
	Coordinates,
	DrawMode,
	Information,
	LineAnnotation,
	Markup,
	MarkupAnnotation,
	MarkupsAnnotation,
	MetadataDrawMode,
	MetadataRectangle,
	Metrics,
	Point,
	Positions,
	Rectangle,
	RedactAnnotation,
	ReplyToAnnotation,
	ReplyToAnnotationEdit,
	RubberStampAnnotation,
	SelectionAnnotation,
	Settings,
	SquareAnnotation,
	TextAnnotation,
	TextAnnotationIcons,
	ToolboxAnnotationAnnotation
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import {AnnotationSubType} from "../../../viewer/pdf/annotation/AnnotationSubType";
import Immutable from "../../../../shared/immutable/Immutable";
import TreeNode from "primereact/treenode";
import {MenuItem} from "primereact/menuitem";
import Parameter from "../../../dialog/parameter/Parameter";
import {WebServiceType, WebServiceTypes} from "@softvision/webpdf-wsclient-9-typescript";
import Exception from "../../../../shared/exception/Exception";
import ErrorCode from "../../../../shared/exception/ErrorCode";
import Message from "../../../../shared/log/Message";
import {MessageType} from "../../../../shared/log/MessageInterface";
import Translate from "../../../../shared/translation/Translate";
import MenuButton from "../../../../shared/menu/MenuButton";
import {DeleteAnnotationDialogDefinition} from "../../../dialog/components/annotation/DeleteAnnotationDialog";
import PortalDialogType from "../../../dialog/PortalDialogType";
import PortalDialogDefault from "../../../dialog/parameter/PortalDialogDefault";
import DialogDefault from "../../../dialog/parameter/PortalDialogDefault";
import {AnnotationMarkEditDialogDefinition} from "../../../dialog/components/annotation/AnnotationMarkEditDialog";
import {AnnotationNoteEditDialogDefinition} from "../../../dialog/components/annotation/AnnotationNoteEditDialog";
import {AnnotationStrikeEditDialogDefinition} from "../../../dialog/components/annotation/AnnotationStrikeEditDialog";
import {
	AnnotationUnderlineEditDialogDefinition
} from "../../../dialog/components/annotation/AnnotationUnderlineEditDialog";
import {
	AnnotationSquigglyEditDialogDefinition
} from "../../../dialog/components/annotation/AnnotationSquigglyEditDialog";
import DomHelper from "../../../../shared/helper/DomHelper";
import {AnnotationLineEditDialogDefinition} from "../../../dialog/components/annotation/AnnotationLineEditDialog";
import {AnnotationSquareEditDialogDefinition} from "../../../dialog/components/annotation/AnnotationSquareEditDialog";
import {AnnotationCircleEditDialogDefinition} from "../../../dialog/components/annotation/AnnotationCircleEditDialog";
import SidebarFunctions, {SidebarFunctionGlobals} from "./SidebarFunctions";
import {PortalGlobals} from "../../../Portal";
import Resizeable, {ResizeableDirection} from "../../../../shared/components/Resizeable";
import {RedactEditDialogDefinition} from "../../../dialog/components/redact/RedactEditDialog";
import {AnnotationReplyFunctionDefinition} from "./annotation/AnnotationReplyFunction";
import {AnnotationReplyEditDialogDefinition} from "../../../dialog/components/annotation/AnnotationReplyEditDialog";
import {AnnotationStampEditDialogDefinition} from "../../../dialog/components/annotation/AnnotationStampEditDialog";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";
import {DebugGlobals} from "../../../dialog/components/settings/DebugDialog";
import UnitHelper from "../../../../shared/helper/UnitHelper";
import LengthUnit from "../../../../shared/units/LengthUnit";
import {HelperGlobals} from "../../../../shared/components/Helper";
import {FormLayerGlobals} from "../../../viewer/pdf/form/FormLayerInterface";
import PortalDocumentManager from "../../PortalDocumentManager";
import PortalComponent from "../../../../shared/components/PortalComponent";
import EscapeHelper from "../../../../shared/helper/EscapeHelper";

enum SortType {
	TYPE = "type",
	DATE = "date",
	AUTHOR = "author",
	PAGE = "page",
	COLOR = "color"
}

enum SortOrder {
	ASC = "ASC",
	DESC = "DESC"
}

enum TreeType {
	STATE = "state",
	MARK = "mark",
	COMMENT = "comment",
	ANNOTATION = "annotation",
	CATEGORY = "category"
}

interface AnnotationListProperties extends SidebarFunctionGlobals, I18nGlobals, PortalGlobals, DebugGlobals,
	HelperGlobals, FormLayerGlobals {
	visible: boolean;
	portalDocument?: Immutable<PortalDocumentInterface>;
	locationObserver?: Observer<LocationEvent>;
}

interface AnnotationListStatus {
	annotations: Array<TreeNode>;
	expanded: TreeExpandedKeysType;
	searchbar: boolean;
	query: string;
	sortType: SortType;
	sortOrder: SortOrder;
	filterAuthor: Array<string>;
	filterColor: Array<string>;
	filterType: Array<string>;
	annotationSelection: string | null;
	sidebarFunctionsHeight?: string;
}

export default class AnnotationList extends PortalComponent<AnnotationListProperties, AnnotationListStatus> {
	private nodeCount: number;
	private annotationList: RefObject<HTMLDivElement>;
	private body: RefObject<HTMLDivElement>;
	private readonly hiddenAnnotations: Array<AnnotationSubType> = [
		AnnotationSubType.WIDGET,
		AnnotationSubType.LINK
	];

	/**
	 * Annotation list component constructor
	 */
	constructor(props: Readonly<AnnotationListProperties>) {
		super(props);

		this.state = {
			annotations: [],
			expanded: {},
			searchbar: false,
			query: "",
			sortType: SortType.PAGE,
			sortOrder: SortOrder.ASC,
			filterAuthor: [],
			filterColor: [],
			filterType: [],
			annotationSelection: null
		};
		this.nodeCount = 1;
		this.annotationList = createRef<HTMLDivElement>();
		this.body = createRef<HTMLDivElement>();

		this.expandList = this.expandList.bind(this);
		this.collapseList = this.collapseList.bind(this);
		this.handleSelect = this.handleSelect.bind(this);
		this.handleKeydown = this.handleKeydown.bind(this);
		this.buildAnnotationList = this.buildAnnotationList.bind(this);
		this.renderNode = this.renderNode.bind(this);
		this.isEditDisabled = this.isEditDisabled.bind(this);
		this.buildEditCommand = this.buildEditCommand.bind(this);
		this.scrollToSelection = this.scrollToSelection.bind(this);
	}

	/**
	 * This is called when the components props or state is updated
	 */
	public async componentDidUpdate(prevProps: Readonly<any>, prevState: Readonly<any>, snapshot?: any): Promise<void> {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let documentChanged: boolean = !this.props.portalDocument!.equals(prevProps.portalDocument);
		if (documentChanged || this.props.language !== prevProps.language) {
			this.buildAnnotationList();
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);
		let documentSettings: DocumentSettings = portalDocument.getSettings();
		if (documentSettings.annotationSelection !== this.state.annotationSelection) {
			this.setState({
				annotationSelection: documentSettings.annotationSelection
			}, this.scrollToSelection);
		}
	}

	/**
	 * This is called when the component is mounted
	 */
	public async componentDidMount(): Promise<void> {
		await super.componentDidMount();
		this.buildAnnotationList();
		this.expandList();

		window.addEventListener('keydown', this.handleKeydown);
	}

	/**
	 * This is called when the component is unmounted
	 */
	public async componentWillUnmount(): Promise<void> {
		await super.componentWillUnmount();

		window.removeEventListener('keydown', this.handleKeydown);
	}

	private scrollToSelection(): void {
		if (this.state.annotationSelection === null || this.annotationList.current === null) {
			return;
		}

		let expanded: any = this.state.expanded;
		let itemElement: HTMLElement | null = null;

		categoryLoop: for (let category of this.state.annotations) {
			for (let item of FunctionHelper.applyValue(category.children, [])) {
				if (item.key === this.state.annotationSelection && FunctionHelper.exists(category.key)) {
					expanded[category.key!] = true;

					itemElement = DomHelper.find(
						this.annotationList.current,
						"#annotation-" + item.key?.replace(" ", "-")
					);
					break categoryLoop;
				}
			}
		}

		this.setState({
			expanded: expanded
		}, () => {
			if (itemElement !== null && this.annotationList.current !== null) {
				let listRect = this.annotationList.current.getBoundingClientRect();
				this.annotationList.current.scrollTop = itemElement.offsetTop - (listRect.height / 2);
			}
		});
	}

	/**
	 * Filter the given annotations with the selected filter and search query
	 */
	private filterAnnotations(annotations: Array<Annotation>): Array<Annotation> {
		let filteredAnnotations: Array<Annotation> = [];
		let escapedQuery: string = EscapeHelper.escapeRegex(this.state.query);
		let expression: RegExp = new RegExp(escapedQuery, 'i');

		annotationLoop: for (let annotation of annotations) {
			// apply color filter
			for (let color of this.state.filterColor) {
				if (FunctionHelper.applyValue(annotation.color, Annotation.getColorDefault()) === color) {
					continue annotationLoop;
				}
			}

			// apply author filter
			for (let author of this.state.filterAuthor) {
				if (FunctionHelper.applyValue(annotation.markup?.creator, Markup.getCreatorDefault()) === author) {
					continue annotationLoop;
				}
			}

			// apply type filter
			for (let type of this.state.filterType) {
				if (FunctionHelper.applyValue(annotation.subType, Annotation.getSubTypeDefault()) === type) {
					continue annotationLoop;
				}
			}

			let annotationContents: string = FunctionHelper.applyValue(
				!FunctionHelper.isEmpty(annotation.contents) ?
					annotation.contents : annotation.markup?.subject, ""
			);

			// apply search filter
			if (this.state.query !== "" && !expression.test(
					FunctionHelper.applyValue(annotation.markup?.subject, Markup.getSubjectDefault())
				) &&
				!expression.test(
					FunctionHelper.applyValue(annotation.markup?.creator, Markup.getCreatorDefault())
				) &&
				!expression.test(annotationContents) &&
				!expression.test(annotation.subType!)) {
				continue;
			}

			filteredAnnotations.push(annotation);
		}

		return filteredAnnotations;
	}

	/**
	 * Sort the given annotations with the selected sort type
	 */
	private sortAnnotations(data: Array<Annotation>): Array<Annotation> {
		let annotations: Array<Annotation> = data;

		// always sort by date
		annotations.sort((obj1: Annotation, obj2: Annotation) => {
			let date1: Date = new Date(
				FunctionHelper.applyValue(obj1.markup?.creationDate, Markup.getCreationDateDefault())
			);
			let date2: Date = new Date(
				FunctionHelper.applyValue(obj2.markup?.creationDate, Markup.getCreationDateDefault())
			);
			return (isNaN(date1.getTime()) ? date1.getTime() : new Date().getTime()) -
				(isNaN(date2.getTime()) ? date2.getTime() : new Date().getTime());
		});

		annotations.sort((obj1: Annotation, obj2: Annotation) => {
			switch (this.state.sortType) {
				case SortType.DATE:
					return 0; // already sorted
				case SortType.AUTHOR:
					return SortHelper.alphanum(
						FunctionHelper.applyValue(obj1.markup?.creator, Markup.getCreatorDefault()),
						FunctionHelper.applyValue(obj2.markup?.creator, Markup.getCreatorDefault())
					);
				case SortType.PAGE:
					return obj1.page! - obj2.page!;
				case SortType.COLOR:
					return SortHelper.alphanum(
						FunctionHelper.applyValue(obj1.color, Annotation.getColorDefault()),
						FunctionHelper.applyValue(obj2.color, Annotation.getColorDefault())
					);
				case SortType.TYPE:
					let type1: string = Translate.value(
						"annotation.types." + obj1.subType!.toLowerCase(), {ns: "sidebar"}
					);
					let type2: string = Translate.value(
						"annotation.types." + obj2.subType!.toLowerCase(), {ns: "sidebar"}
					);
					return SortHelper.alphanum(type1, type2);
				default:
					return 0; // unknown sort type return neutral value
			}
		});

		if (this.state.sortOrder !== SortOrder.ASC) {
			annotations.reverse();
		}

		return annotations;
	}

	private buildReplyAnnotations(key?: string): Array<TreeNode> {
		if (!FunctionHelper.exists(key) || !FunctionHelper.exists(this.props.portalDocument?.value)) {
			return [];
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);
		let annotations: Array<Annotation> = FunctionHelper.applyValue(
			portalDocument.metadata?.annotations?.annotation, AnnotationsDocument.getAnnotationDefault()
		);

		let childs: Array<TreeNode> = [];
		for (let annotation of annotations) {
			let parentId: string | undefined = annotation.markup?.relation?.state?.objectID ||
				annotation.markup?.relation?.mark?.objectID || annotation.markup?.relation?.comment?.objectID;

			if (!FunctionHelper.exists(parentId) || parentId !== key) {
				continue;
			}

			let type: TreeType = TreeType.COMMENT;
			if (FunctionHelper.exists(annotation.markup?.relation?.state)) {
				type = TreeType.STATE;
				continue;
			} else if (FunctionHelper.exists(annotation.markup?.relation?.mark)) {
				type = TreeType.MARK;
				continue;
			}

			childs.push({
				key: annotation.objectKey,
				label: annotation.name,
				data: {
					...annotation,
					type: type
				},
				icon: "icon " + this.getAnnotationIcon(AnnotationSubType.REPLY),
				children: this.buildReplyAnnotations(annotation.objectKey)
			} as TreeNode)
		}

		return childs;
	}

	/**
	 * Build annotation data
	 */
	private buildAnnotationList(): void {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);
		let annotations: Array<Annotation> = FunctionHelper.applyValue(
			portalDocument.metadata?.annotations?.annotation, AnnotationsDocument.getAnnotationDefault()
		);
		let categories: Map<string, Array<TreeNode>> = new Map<string, Array<TreeNode>>();
		annotations = this.filterAnnotations(annotations);
		annotations = this.sortAnnotations(annotations);

		this.nodeCount = 1;
		for (let annotation of annotations) {
			if (!Object.values(AnnotationSubType).map((value) => value.toLowerCase()).includes(
				FunctionHelper.applyValue(annotation.subType?.toLowerCase(), Annotation.getSubTypeDefault())
			) || this.hiddenAnnotations.map((value) => value.toLowerCase()).includes(
				FunctionHelper.applyValue(annotation.subType?.toLowerCase(), Annotation.getSubTypeDefault())
			)) {
				// ignore all unknown or hidden annotations
				continue;
			}

			let parentId: string | undefined = annotation.markup?.relation?.state?.objectID ||
				annotation.markup?.relation?.mark?.objectID || annotation.markup?.relation?.comment?.objectID;

			if (FunctionHelper.exists(parentId)) {
				continue;
			}

			let key = "";
			switch (this.state.sortType) {
				case SortType.TYPE:
					if (FunctionHelper.exists(annotation.subType)) {
						key = Translate.value(
							"annotation.types." + annotation.subType!.toLowerCase(), {ns: "sidebar"}
						);
					}
					break;
				case SortType.AUTHOR:
					if (FunctionHelper.exists(annotation.markup?.creator)) {
						key = annotation.markup!.creator!;
					}
					break;
				case SortType.COLOR:
					if (FunctionHelper.exists(annotation.color)) {
						key = annotation.color!;
					}
					break;
				case SortType.PAGE:
					if (FunctionHelper.exists(annotation.page)) {
						key = Translate.value(
							"annotation.page", {ns: "sidebar", page: annotation.page}
						);
					}
					break;
			}

			if (!categories.has(key)) {
				categories.set(key, []);
			}

			categories.get(key)!.push({
				key: annotation.objectKey,
				label: annotation.name,
				data: {
					...annotation,
					type: TreeType.ANNOTATION
				},
				icon: "icon " + this.getAnnotationIcon(annotation.subType),
				children: this.buildReplyAnnotations(annotation.objectKey)
			} as TreeNode);

			this.nodeCount++;
		}

		let result: Array<TreeNode> = [];
		for (let [key, category] of categories.entries()) {
			result.push({
				key: key,
				data: {
					type: TreeType.CATEGORY
				},
				label: key + " (" + category.length + ")",
				children: category
			} as TreeNode);
		}

		this.setState({
			annotations: result
		}, () => {
			this.setState({
				expanded: this.expandReplyAnnotations()
			});
		});
	}

	private expandReplyAnnotations(): TreeExpandedKeysType {
		let expanded: TreeExpandedKeysType = this.state.expanded;

		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return expanded;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);

		for (let category of this.state.annotations) {
			for (let child of FunctionHelper.applyValue(category.children, [])) {
				let annotations: Array<Annotation> =
					portalDocument.getAnnotation(child.key as string, true);

				for (let annotation of annotations) {
					expanded[annotation.objectKey!] = true;
				}
			}
		}

		return expanded;
	}

	/**
	 * returns the computed annotation boundaries für all types with appearance
	 */
	private getAnnotationBoundaries(annotation: Annotation): MetadataRectangle {
		let positions: Array<Positions> = FunctionHelper.applyValue(
			annotation.positions, Annotation.getPositionsDefault()
		);
		let result: MetadataRectangle | null = null;

		for (let position of positions) {
			if (FunctionHelper.exists((position as AnnotationPoint).point)) {
				let point: AnnotationPointPoint = (position as AnnotationPoint).point!;

				if (result === null) {
					result = MetadataRectangle.fromJson({
						width: (annotation.border && annotation.border.width) ? annotation.border.width : 1,
						height: (annotation.border && annotation.border.width) ? annotation.border.width : 1,
						x: point.x,
						y: point.y
					} as MetadataRectangle);
				}

				if (point.x! < result.x!) {
					result.width! += result.x! - point.x!;
					result.x = point.x;
				}

				if (point.y! < result.y!) {
					result.height! += result.y! - point.y!;
					result.y = point.y;
				}

				if (point.x! - result.x! > result.width!) {
					result.width = point.x! - result.x!;
				}

				if (point.y! - result.y! > result.height!) {
					result.height = point.y! - result.y!;
				}
			} else if (FunctionHelper.exists((position as AnnotationRectangle).rectangle)) {
				let rectangle: AnnotationRectangleRectangle =
					(position as AnnotationRectangle).rectangle!;

				if (result === null) {
					result = MetadataRectangle.fromJson({
						width: rectangle.width,
						height: rectangle.height,
						x: rectangle.x,
						y: rectangle.y
					} as MetadataRectangle);
				}

				if (rectangle.x! < result.x!) {
					result.x = rectangle.x;
				}

				if (rectangle.y! < result.y!) {
					result.y = rectangle.y;
				}

				if (rectangle.width! > result.width!) {
					result.width = rectangle.width;
				}

				if (rectangle.height! > result.height!) {
					result.height = rectangle.height;
				}
			}
		}

		if (result === null) {
			result = MetadataRectangle.fromJson({
				width: 0,
				height: 0,
				x: 0,
				y: 0
			} as MetadataRectangle);
		}

		result = MetadataRectangle.fromJson({
			width: UnitHelper.unitToPx(result.width!, LengthUnit.POINTS, this.props.portalResolution),
			height: UnitHelper.unitToPx(result.height!, LengthUnit.POINTS, this.props.portalResolution),
			x: UnitHelper.unitToPx(result.x!, LengthUnit.POINTS, this.props.portalResolution),
			y: UnitHelper.unitToPx(result.y!, LengthUnit.POINTS, this.props.portalResolution)
		} as MetadataRectangle);

		return result;
	};

	/**
	 * returns the annotation icon class
	 */
	private getAnnotationIcon(type?: string): string {
		switch (type) {
			case AnnotationSubType.FILEATTACHMENT:
				return "icon-attachment small";
			case AnnotationSubType.STAMP:
				return "icon-annotation-stamp";
			case AnnotationSubType.POLYGON:
				return "icon-annotation-polygon";
			case AnnotationSubType.POLYLINE:
				return "icon-annotation-polyline";
			case AnnotationSubType.FREETEXT:
				return "icon-annotation-text";
			case AnnotationSubType.SQUARE:
				return "icon-annotation-square";
			case AnnotationSubType.CIRCLE:
				return "icon-annotation-circle";
			case AnnotationSubType.STRIKEOUT:
				return "icon-annotation-strike";
			case AnnotationSubType.UNDERLINE:
				return "icon-annotation-underline";
			case AnnotationSubType.SQUIGGLY:
				return "icon-annotation-squiggly";
			case AnnotationSubType.HIGHLIGHT:
				return "icon-annotation-mark";
			case AnnotationSubType.TEXT:
				return "icon-annotation-note";
			case AnnotationSubType.LINE:
				return "icon-annotation-line";
			case AnnotationSubType.INK:
				return "icon-annotation-pencil";
			case AnnotationSubType.REDACT:
				return "icon-redact-text";
			case AnnotationSubType.REPLY:
				return "icon-reply";
			default:
				return "icon-help";
		}
	}

	private expandCategory(target?: string | number): TreeExpandedKeysType {
		let expanded: TreeExpandedKeysType = this.state.expanded;

		for (let category of this.state.annotations) {
			if (category.key !== target) {
				continue;
			}

			expanded[category.key!] = true;
			break;
		}

		return expanded;
	}

	private collapseCategory(target?: string | number): TreeExpandedKeysType {
		let expanded: TreeExpandedKeysType = this.state.expanded;

		for (let category of this.state.annotations) {
			if (category.key !== target) {
				continue;
			}

			delete expanded[category.key!];
			break;
		}

		return expanded;
	}

	/**
	 * Expands all Tree entries
	 */
	private expandList(): void {
		let expanded: TreeExpandedKeysType = this.state.expanded;

		for (let category of this.state.annotations) {
			this.expandCategory(category.key);
		}

		this.setState({
			expanded: expanded
		});
	}

	/**
	 * Collapses all Tree entries
	 */
	private collapseList(): void {
		let expanded: TreeExpandedKeysType = this.state.expanded;

		for (let category of this.state.annotations) {
			this.collapseCategory(category.key);
		}

		this.setState({
			expanded: expanded
		});
	}

	/**
	 * Handles keyboard key down events
	 */
	private handleKeydown(e: KeyboardEvent): void {
		if (this.state.searchbar && e.key.toLowerCase() === "enter") {
			this.buildAnnotationList();
		}
	}

	/**
	 * This is called if a tree node is selected
	 */
	private async handleSelect(event: TreeEventNodeEvent): Promise<void> {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let key: string = event.node.key as string;
		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);
		let documentSettings: DocumentSettings = portalDocument.getSettings();

		switch (event.node.data.type) {
			case TreeType.ANNOTATION:
				if (documentSettings.annotationSelection === key) {
					documentSettings.annotationSelection = null;
					this.props.context.documentManager.updateGlobalDocumentList();
					return;
				}

				documentSettings.annotationSelection = key;
				this.props.context.documentManager.updateGlobalDocumentList();

				if (FunctionHelper.exists(this.props.locationObserver)) {
					this.props.locationObserver!.notify({
						target: this.getAnnotationBoundaries(event.node.data),
						pageNumber: event.node.data.page
					})
				}
				break;
			case TreeType.CATEGORY:
				let expanded: TreeExpandedKeysType = this.state.expanded;

				if (FunctionHelper.exists(expanded[key])) {
					this.collapseCategory(key);
				} else {
					this.expandCategory(key);
				}

				this.setState({
					expanded: expanded
				});
				break;
		}
	}

	/**
	 * toggle a given sort type or if already selected change the sort order
	 */
	private toggleSort(sortType: SortType): void {
		let sortOrder: SortOrder = this.state.sortOrder;

		if (sortType === this.state.sortType) {
			sortOrder = this.state.sortOrder === SortOrder.DESC ? SortOrder.ASC : SortOrder.DESC;
		}

		this.setState({
			sortType: sortType,
			sortOrder: sortOrder
		}, this.buildAnnotationList);
	}

	/**
	 * returns the sort order icon class
	 */
	private getSortOrderIcon(sortType: SortType): string {
		if (sortType === this.state.sortType) {
			return "icon " + (this.state.sortOrder === SortOrder.DESC ? "icon-sort-desc" : "icon-sort-asc")
		}

		return "icon empty";
	}

	private checkPasswords(): boolean {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return false;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);

		if (portalDocument.hasPasswordProtection("permission") &&
			(!portalDocument.getDocumentFile()?.password?.isPermissionSet || portalDocument.hasError())
		) {
			return true;
		}

		return false;
	}

	/**
	 * Delete the currently selected annotation
	 * @private
	 */
	private async deleteAnnotation(deleteParameter: Parameter<ToolboxAnnotationAnnotation>): Promise<void> {
		if (this.checkPasswords()) {
			this.props.context.getPage().setState({
				showPasswordDialog: true,
				passwordRecallFunction: async () => {
					await this.deleteAnnotation(deleteParameter);
				}
			});

			return;
		}

		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);

		let toolboxOperation: BaseToolbox = BaseToolbox.fromJson({
			annotation: deleteParameter.data.toJson()
		});
		let operationData: Array<BaseToolbox> = [toolboxOperation];
		let webServiceType: WebServiceType = WebServiceTypes.TOOLBOX;

		try {
			await this.props.context.session.executeRequest({
				webServiceType: webServiceType,
				document: portalDocument,
				operation: operationData,
				settings: Settings.fromJson({
					pdfa: {
						pdfaEditProfile: this.props.pdfaEditProfile
					},
					signature: {
						signatureEditProfile: this.props.signatureEditProfile
					},
					compress: {
						compressProfile: this.props.compressProfile,
						compressObjects: {
							objectStreamSize: this.props.objectStreamSize
						}
					}
				} as Settings)
			});
		} catch (exception: any) {
			let portalException: Exception = Exception.parseException(exception);
			if (portalException.code === ErrorCode.WEBSERVICE_SESSION_TIMEOUT) {
				await this.deleteAnnotation(deleteParameter);
			}
			return;
		}

		let message: Message = new Message("success.toolbox.annotation.remove", MessageType.SUCCESS);
		this.props.context.getPage().addMessage(message);
	}

	private isEditDisabled(annotation: Annotation): boolean {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return true;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);
		let portalInfo: Information = this.props.context.info;

		let parentId: string | undefined = annotation.markup?.relation?.state?.objectID ||
			annotation.markup?.relation?.mark?.objectID || annotation.markup?.relation?.comment?.objectID;
		if (FunctionHelper.exists(parentId)) {
			return !AnnotationReplyEditDialogDefinition.isActive(
				[portalDocument], portalInfo, this.props.signatureEditProfile
			);
		}

		switch (annotation.subType) {
			case AnnotationSubType.HIGHLIGHT:
				return !AnnotationMarkEditDialogDefinition.isActive(
					[portalDocument], portalInfo, this.props.signatureEditProfile
				);
			case AnnotationSubType.TEXT:
				return !AnnotationNoteEditDialogDefinition.isActive(
					[portalDocument], portalInfo, this.props.signatureEditProfile
				);
			case AnnotationSubType.STRIKEOUT:
				return !AnnotationStrikeEditDialogDefinition.isActive(
					[portalDocument], portalInfo, this.props.signatureEditProfile
				);
			case AnnotationSubType.UNDERLINE:
				return !AnnotationUnderlineEditDialogDefinition.isActive(
					[portalDocument], portalInfo, this.props.signatureEditProfile
				);
			case AnnotationSubType.SQUIGGLY:
				return !AnnotationSquigglyEditDialogDefinition.isActive(
					[portalDocument], portalInfo, this.props.signatureEditProfile
				);
			case AnnotationSubType.LINE:
				return !AnnotationLineEditDialogDefinition.isActive(
					[portalDocument], portalInfo, this.props.signatureEditProfile
				);
			case AnnotationSubType.SQUARE:
				return !AnnotationSquareEditDialogDefinition.isActive(
					[portalDocument], portalInfo, this.props.signatureEditProfile
				);
			case AnnotationSubType.CIRCLE:
				return !AnnotationCircleEditDialogDefinition.isActive(
					[portalDocument], portalInfo, this.props.signatureEditProfile
				);
			case AnnotationSubType.STAMP:
				return !AnnotationStampEditDialogDefinition.isActive(
					[portalDocument], portalInfo, this.props.signatureEditProfile
				);
			case AnnotationSubType.REDACT:
				return !RedactEditDialogDefinition.isActive(
					[portalDocument], portalInfo, this.props.signatureEditProfile
				);
			default:
				return true; // unknown annotation can't be edited
		}
	}

	private async buildEditCommand(annotation: Annotation): Promise<void> {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		if (DocumentSettings.checkActiveMouseFunction(this.props)) {
			DocumentSettings.confirmResetMouseFunction(this.props, async () => {
				await this.buildEditCommand(annotation);
			});
			return;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);

		let parentId: string | undefined = annotation.markup?.relation?.state?.objectID ||
			annotation.markup?.relation?.mark?.objectID || annotation.markup?.relation?.comment?.objectID;
		if (FunctionHelper.exists(parentId)) {
			let annotationReplyParameter: Parameter<ToolboxAnnotationAnnotation> =
				DialogDefault.getParameter(PortalDialogType.ANNOTATION_REPLY_EDIT, [portalDocument]);

			annotationReplyParameter.data.comment!.edit!.replyTo = [
				ReplyToAnnotationEdit.fromJson({
					creator: annotation.markup?.creator,
					subject: annotation.markup?.subject,
					comment: annotation.contents,
					page: annotation.page,
					objectKey: {
						id: annotation.objectKey
					}
				} as ReplyToAnnotationEdit)
			];

			this.props.context.getPage().setState({
				annotationReplyEditParameter: annotationReplyParameter,
				showAnnotationReplyEditDialog: true
			});
			return;
		}

		switch (annotation.subType) {
			case AnnotationSubType.HIGHLIGHT:
				let annotationMarkParameter: Parameter<ToolboxAnnotationAnnotation> =
					DialogDefault.getParameter(PortalDialogType.ANNOTATION_MARK_EDIT, [portalDocument]);

				annotationMarkParameter.data.edit!.selection.select = [
					new SelectionAnnotation({
						objectKey: {
							id: annotation.objectKey
						},
						page: annotation.page
					} as SelectionAnnotation)
				];

				annotationMarkParameter.data.edit!.markup = MarkupAnnotation.fromJson({
					color: annotation.color,
					creator: annotation.markup?.creator,
					contents: annotation.contents,
					opacity: annotation.markup?.opacity,
					page: annotation.page,
					subject: annotation.markup?.subject,
					intents: annotation.markup?.intents,
					markupType: MarkupsAnnotation.Highlight
				} as MarkupAnnotation);

				this.props.context.getPage().setState({
					annotationMarkEditParameter: annotationMarkParameter,
					showAnnotationMarkEditDialog: true
				});
				break;
			case AnnotationSubType.TEXT:
				let annotationNoteParameter: Parameter<ToolboxAnnotationAnnotation> =
					DialogDefault.getParameter(PortalDialogType.ANNOTATION_NOTE_EDIT, [portalDocument]);

				annotationNoteParameter.data.edit!.selection.select = [
					new SelectionAnnotation({
						objectKey: {
							id: annotation.objectKey
						},
						page: annotation.page
					} as SelectionAnnotation)
				];

				let icon: TextAnnotationIcons = TextAnnotationIcons.Comment;
				for (let value of Object.values(TextAnnotationIcons)) {
					if (annotation.iconName?.toLowerCase() === value.toLowerCase()) {
						icon = value as TextAnnotationIcons;
						break;
					}
				}

				annotationNoteParameter.data.edit!.text = TextAnnotation.fromJson({
					color: annotation.color,
					creator: annotation.markup?.creator,
					contents: annotation.contents,
					opacity: annotation.markup?.opacity,
					page: annotation.page,
					subject: annotation.markup?.subject,
					intents: annotation.markup?.intents,
					icon: icon
				} as TextAnnotation);

				this.props.context.getPage().setState({
					annotationNoteEditParameter: annotationNoteParameter,
					showAnnotationNoteEditDialog: true
				});
				break;
			case AnnotationSubType.STRIKEOUT:
				let annotationStrikeParameter: Parameter<ToolboxAnnotationAnnotation> =
					DialogDefault.getParameter(PortalDialogType.ANNOTATION_STRIKE_EDIT, [portalDocument]);

				annotationStrikeParameter.data.edit!.selection.select = [
					new SelectionAnnotation({
						objectKey: {
							id: annotation.objectKey
						},
						page: annotation.page
					} as SelectionAnnotation)
				];

				annotationStrikeParameter.data.edit!.markup = MarkupAnnotation.fromJson({
					color: annotation.color,
					creator: annotation.markup?.creator,
					contents: annotation.contents,
					opacity: annotation.markup?.opacity,
					page: annotation.page,
					subject: annotation.markup?.subject,
					intents: annotation.markup?.intents,
					markupType: MarkupsAnnotation.StrikeOut
				} as MarkupAnnotation);

				this.props.context.getPage().setState({
					annotationStrikeEditParameter: annotationStrikeParameter,
					showAnnotationStrikeEditDialog: true
				});
				break;
			case AnnotationSubType.UNDERLINE:
				let annotationUnderlineParameter: Parameter<ToolboxAnnotationAnnotation> =
					DialogDefault.getParameter(PortalDialogType.ANNOTATION_UNDERLINE_EDIT, [portalDocument]);

				annotationUnderlineParameter.data.edit!.selection.select = [
					new SelectionAnnotation({
						objectKey: {
							id: annotation.objectKey
						},
						page: annotation.page
					} as SelectionAnnotation)
				];

				annotationUnderlineParameter.data.edit!.markup = MarkupAnnotation.fromJson({
					color: annotation.color,
					creator: annotation.markup?.creator,
					contents: annotation.contents,
					opacity: annotation.markup?.opacity,
					page: annotation.page,
					subject: annotation.markup?.subject,
					intents: annotation.markup?.intents,
					markupType: MarkupsAnnotation.Underline
				} as MarkupAnnotation);

				this.props.context.getPage().setState({
					annotationUnderlineEditParameter: annotationUnderlineParameter,
					showAnnotationUnderlineEditDialog: true
				});
				break;
			case AnnotationSubType.SQUIGGLY:
				let annotationSquigglyParameter: Parameter<ToolboxAnnotationAnnotation> =
					DialogDefault.getParameter(PortalDialogType.ANNOTATION_SQUIGGLY_EDIT, [portalDocument]);

				annotationSquigglyParameter.data.edit!.selection.select = [
					new SelectionAnnotation({
						objectKey: {
							id: annotation.objectKey
						},
						page: annotation.page
					} as SelectionAnnotation)
				];

				annotationSquigglyParameter.data.edit!.markup = MarkupAnnotation.fromJson({
					color: annotation.color,
					creator: annotation.markup?.creator,
					contents: annotation.contents,
					opacity: annotation.markup?.opacity,
					page: annotation.page,
					subject: annotation.markup?.subject,
					intents: annotation.markup?.intents,
					markupType: MarkupsAnnotation.Squiggly
				} as MarkupAnnotation);

				this.props.context.getPage().setState({
					annotationSquigglyEditParameter: annotationSquigglyParameter,
					showAnnotationSquigglyEditDialog: true
				});
				break;
			case AnnotationSubType.LINE:
				let annotationLineParameter: Parameter<ToolboxAnnotationAnnotation> =
					DialogDefault.getParameter(PortalDialogType.ANNOTATION_LINE_EDIT, [portalDocument]);

				annotationLineParameter.data.edit!.selection.select = [
					new SelectionAnnotation({
						objectKey: {
							id: annotation.objectKey
						},
						page: annotation.page
					} as SelectionAnnotation)
				];

				let lineStyle: BorderStyleEnum = BorderStyleEnum.Solid;
				switch (annotation.border?.style as BorderStyle) {
					case BorderStyle.Dashed:
						lineStyle = BorderStyleEnum.Dashed;
						break;
				}

				let lineStart: Point = Point.fromJson({
					...(annotation.positions![0] as AnnotationPoint).point!.toJson(),
					metrics: Metrics.Mm,
					coordinates: Coordinates.Pdf
				} as Point);
				let lineEnd: Point = Point.fromJson({
					...(annotation.positions![1] as AnnotationPoint).point!.toJson(),
					metrics: Metrics.Mm,
					coordinates: Coordinates.Pdf
				} as Point);

				annotationLineParameter.data.edit!.line = LineAnnotation.fromJson({
					color: annotation.color,
					creator: annotation.markup?.creator,
					contents: annotation.contents,
					opacity: annotation.markup?.opacity,
					page: annotation.page,
					subject: annotation.markup?.subject,
					intents: annotation.markup?.intents,
					start: lineStart,
					end: lineEnd,
					border: {
						borderWidth: annotation.border?.width,
						borderStyle: lineStyle
					}
				} as LineAnnotation);

				this.props.context.getPage().setState({
					annotationLineEditParameter: annotationLineParameter,
					showAnnotationLineEditDialog: true
				});
				break;
			case AnnotationSubType.SQUARE:
				let annotationSquareParameter: Parameter<ToolboxAnnotationAnnotation> =
					DialogDefault.getParameter(PortalDialogType.ANNOTATION_SQUARE_EDIT, [portalDocument]);

				annotationSquareParameter.data.edit!.selection.select = [
					new SelectionAnnotation({
						objectKey: {
							id: annotation.objectKey
						},
						page: annotation.page
					} as SelectionAnnotation)
				];

				let squareStyle: BorderStyleEnum = BorderStyleEnum.Solid;
				switch (annotation.border?.style as BorderStyle) {
					case BorderStyle.Dashed:
						squareStyle = BorderStyleEnum.Dashed;
						break;
				}

				let squareRect: Rectangle = Rectangle.fromJson({
					...(annotation.positions![0] as AnnotationRectangle).rectangle!.toJson(),
					metrics: Metrics.Mm,
					coordinates: Coordinates.Pdf
				} as Rectangle);

				let squareDrawMode: DrawMode = DrawMode.FillAndStroke;
				switch (annotation.drawStyle?.drawMode) {
					case MetadataDrawMode.Fill:
						squareDrawMode = DrawMode.Fill;
						break;
					case MetadataDrawMode.Stroke:
						squareDrawMode = DrawMode.Stroke;
						break;
				}

				annotationSquareParameter.data.edit!.square = SquareAnnotation.fromJson({
					color: annotation.color,
					interiorColor: annotation.border?.interiorColor,
					creator: annotation.markup?.creator,
					contents: annotation.contents,
					opacity: annotation.markup?.opacity,
					page: annotation.page,
					subject: annotation.markup?.subject,
					intents: annotation.markup?.intents,
					position: squareRect,
					border: {
						borderWidth: annotation.border?.width,
						borderStyle: squareStyle
					},
					drawMode: squareDrawMode
				} as SquareAnnotation);

				this.props.context.getPage().setState({
					annotationSquareEditParameter: annotationSquareParameter,
					showAnnotationSquareEditDialog: true
				});
				break;
			case AnnotationSubType.CIRCLE:
				let annotationCircleParameter: Parameter<ToolboxAnnotationAnnotation> =
					DialogDefault.getParameter(PortalDialogType.ANNOTATION_CIRCLE_EDIT, [portalDocument]);

				annotationCircleParameter.data.edit!.selection.select = [
					new SelectionAnnotation({
						objectKey: {
							id: annotation.objectKey
						},
						page: annotation.page
					} as SelectionAnnotation)
				];

				let circleStyle: BorderStyleEnum = BorderStyleEnum.Solid;
				switch (annotation.border?.style as BorderStyle) {
					case BorderStyle.Dashed:
						circleStyle = BorderStyleEnum.Dashed;
						break;
				}

				let circleRect: Rectangle = Rectangle.fromJson({
					...(annotation.positions![0] as AnnotationRectangle).rectangle!.toJson(),
					metrics: Metrics.Mm,
					coordinates: Coordinates.Pdf
				} as Rectangle);

				let circleDrawMode: DrawMode = DrawMode.FillAndStroke;
				switch (annotation.drawStyle?.drawMode) {
					case MetadataDrawMode.Fill:
						circleDrawMode = DrawMode.Fill;
						break;
					case MetadataDrawMode.Stroke:
						circleDrawMode = DrawMode.Stroke;
						break;
				}

				annotationCircleParameter.data.edit!.circle = CircleAnnotation.fromJson({
					color: annotation.color,
					interiorColor: annotation.border?.interiorColor,
					creator: annotation.markup?.creator,
					contents: annotation.contents,
					opacity: annotation.markup?.opacity,
					page: annotation.page,
					subject: annotation.markup?.subject,
					intents: annotation.markup?.intents,
					position: circleRect,
					border: {
						borderWidth: annotation.border?.width,
						borderStyle: circleStyle
					},
					drawMode: circleDrawMode
				} as CircleAnnotation);

				this.props.context.getPage().setState({
					annotationCircleEditParameter: annotationCircleParameter,
					showAnnotationCircleEditDialog: true
				});
				break;
			case AnnotationSubType.REDACT:
				let redactEditParameter: Parameter<ToolboxAnnotationAnnotation> =
					DialogDefault.getParameter(PortalDialogType.REDACT_EDIT, [portalDocument]);

				redactEditParameter.data.edit!.selection.select = [
					new SelectionAnnotation({
						objectKey: {
							id: annotation.objectKey
						},
						page: annotation.page
					} as SelectionAnnotation)
				];

				let squareRects: Array<Rectangle> = [];

				for (let position of FunctionHelper.applyValue(
					annotation.positions, Annotation.getPositionsDefault()
				)) {
					squareRects.push(
						Rectangle.fromJson({
							...(position as AnnotationRectangle).rectangle!.toJson(),
							metrics: Metrics.Mm,
							coordinates: Coordinates.Pdf
						} as Rectangle)
					);
				}

				redactEditParameter.data.edit!.redact = RedactAnnotation.fromJson({
					color: annotation.color,
					interiorColor: annotation.border?.interiorColor,
					creator: annotation.markup?.creator,
					contents: annotation.contents,
					opacity: annotation.markup?.opacity,
					page: annotation.page,
					subject: annotation.markup?.subject,
					intents: annotation.markup?.intents,
					position: {
						pathElement: squareRects
					}
				} as RedactAnnotation);

				this.props.context.getPage().setState({
					redactEditParameter: redactEditParameter,
					showRedactEditDialog: true
				});
				break;
			case AnnotationSubType.STAMP:
				let annotationStampParameter: Parameter<ToolboxAnnotationAnnotation> =
					DialogDefault.getParameter(PortalDialogType.ANNOTATION_STAMP_EDIT, [portalDocument]);

				annotationStampParameter.data.edit!.selection.select = [
					new SelectionAnnotation({
						objectKey: {
							id: annotation.objectKey
						},
						page: annotation.page
					} as SelectionAnnotation)
				];

				let stampRect: Rectangle = Rectangle.fromJson({
					...(annotation.positions![0] as AnnotationRectangle).rectangle!.toJson(),
					metrics: Metrics.Mm,
					coordinates: Coordinates.Pdf
				} as Rectangle);

				annotationStampParameter.data.edit!.rubberstamp = RubberStampAnnotation.fromJson({
					color: annotation.color,
					interiorColor: annotation.border?.interiorColor,
					creator: annotation.markup?.creator,
					contents: annotation.contents,
					opacity: annotation.markup?.opacity,
					page: annotation.page,
					subject: annotation.markup?.subject,
					intents: annotation.markup?.intents,
					position: stampRect
				} as CircleAnnotation);

				this.props.context.getPage().setState({
					annotationStampEditParameter: annotationStampParameter,
					showAnnotationStampEditDialog: true
				});
				break;
			default:
				return; // unknown annotation can't be edited
		}
	}

	/**
	 * Render an annotation entry
	 */
	private renderNode(node: any): JSX.Element {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return <></>;
		}

		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let annotation: Annotation = node.data;
		let portalDocument: PortalDocument = documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);
		let documentSettings: DocumentSettings | undefined = portalDocument.getSettings();
		let portalInfo: Information = this.props.context.info;
		let annotationContents: string | undefined = !FunctionHelper.isEmpty(annotation.contents) ?
			annotation.contents : annotation.markup?.subject;

		switch (node.data.type) {
			case TreeType.CATEGORY:
				return node.label;
			case TreeType.ANNOTATION:
				return (
					<>
						<div className="annotation-item" id={"annotation-" + annotation.objectKey?.replace(" ", "-")}>
							<div className="title">
								<span className="color"
									  style={{
										  background: FunctionHelper.applyValue(
											  annotation.color,
											  "linear-gradient(#000 -50%, transparent 100%)"
										  )
									  }}/>
								{/*{annotation.markup?.subject}*/}
							</div>
							<div className="author">{annotation.markup?.creator}</div>
							<div className="content">{annotationContents}&nbsp;</div>
						</div>
						<div className="annotation-functions">
							<MenuButton {...this.props} icon="icon icon-more" disabled={portalDocument.isLocked()}
										onClick={(event) => {
											event.stopPropagation();
										}}
										menuItems={
											[
												{
													label: Translate.value("annotation.reply", {ns: "sidebar"}),
													icon: "icon icon-reply",
													disabled: !AnnotationReplyFunctionDefinition.isActive(
														[portalDocument], portalInfo,
														this.props.signatureEditProfile
													),
													command: async () => {
														let command: () => Promise<void> = async () => {
															let replyParameter: Parameter<ToolboxAnnotationAnnotation> =
																PortalDialogDefault.getParameter(
																	PortalDialogType.ANNOTATION_REPLY, [portalDocument]
																);

															let annotationParameter: ReplyToAnnotation =
																replyParameter.data.comment!.add!.replyTo![0];
															annotationParameter.replyTo = annotation.objectKey;
															annotationParameter.page = annotation.page;

															this.props.context.getPage().setState({
																annotationReplyParameter: replyParameter,
																showAnnotationReplyFunction: true
															}, () => {
																documentSettings!.activeViewer.mouseFunctionActive = true;
																documentManager.updateGlobalDocumentList();
															});
														}

														if (DocumentSettings.checkActiveMouseFunction(this.props)) {
															DocumentSettings.confirmResetMouseFunction(this.props, command);
															return;
														}

														await command();
													}
												},
												{
													label: Translate.value("annotation.edit", {ns: "sidebar"}),
													icon: "icon icon-edit",
													disabled: this.isEditDisabled(annotation),
													command: async () => await this.buildEditCommand(annotation)
												},
												{
													label: Translate.value("annotation.remove", {ns: "sidebar"}),
													icon: "icon icon-delete small",
													command: async () => {
														let command: () => Promise<void> = async () => {
															let deleteParameter: Parameter<ToolboxAnnotationAnnotation> =
																PortalDialogDefault.getParameter(
																	PortalDialogType.ANNOTATION_DELETE, [portalDocument]
																);
															deleteParameter.data.remove!.selection.select = [
																new SelectionAnnotation({
																	objectKey: {
																		id: annotation.objectKey
																	},
																	page: annotation.page
																} as SelectionAnnotation)
															];
															await this.deleteAnnotation(deleteParameter);
														}

														if (DocumentSettings.checkActiveMouseFunction(this.props)) {
															DocumentSettings.confirmResetMouseFunction(this.props, command);
															return;
														}

														await command();
													},
													disabled: !DeleteAnnotationDialogDefinition.isActive(
														[portalDocument], portalInfo, this.props.signatureEditProfile
													)
												}
											]
										}/>
						</div>
					</>
				);
			case TreeType.COMMENT:
				return (
					<>
						<div className="annotation-comment"
							 id={"annotation-" + annotation.objectKey?.replace(" ", "-")}>
							<div className="author">{annotation.markup?.creator}</div>
							<div className="content">{annotation.contents}</div>
						</div>
						<div className="annotation-functions">
							<MenuButton {...this.props} icon="icon icon-more"
										onClick={(event) => {
											event.stopPropagation();
										}}
										menuItems={
											[
												{
													label: Translate.value("annotation.edit", {ns: "sidebar"}),
													icon: "icon icon-edit",
													disabled: this.isEditDisabled(annotation),
													command: async () => await this.buildEditCommand(annotation)
												},
												{
													label: Translate.value("annotation.remove", {ns: "sidebar"}),
													icon: "icon icon-delete small",
													command: async () => {
														let command: () => Promise<void> = async () => {
															let deleteParameter: Parameter<ToolboxAnnotationAnnotation> =
																PortalDialogDefault.getParameter(
																	PortalDialogType.ANNOTATION_DELETE, [portalDocument]
																);
															deleteParameter.data.remove!.selection.select = [
																new SelectionAnnotation({
																	objectKey: {
																		id: annotation.objectKey
																	},
																	page: annotation.page
																} as SelectionAnnotation)
															];
															await this.deleteAnnotation(deleteParameter);
														}

														if (DocumentSettings.checkActiveMouseFunction(this.props)) {
															DocumentSettings.confirmResetMouseFunction(this.props, command);
															return;
														}

														await command();
													},
													disabled: !DeleteAnnotationDialogDefinition.isActive(
														[portalDocument], portalInfo, this.props.signatureEditProfile
													)
												}
											]
										}/>
						</div>
					</>
				);
		}

		return <></>;
	}

	/**
	 * Render the annotation filter menu
	 */
	private renderFilterMenu(): Array<MenuItem> {
		let menuItems: Array<MenuItem> = [];

		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return menuItems;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);
		let annotations: Array<Annotation> = FunctionHelper.applyValue(
			portalDocument.metadata?.annotations?.annotation, AnnotationsDocument.getAnnotationDefault()
		);
		let colors: Array<string> = [];
		let authors: Array<string> = [];
		let types: Array<string> = [];

		for (let annotation of annotations) {
			let color: string = FunctionHelper.applyValue(annotation.color, Annotation.getColorDefault());
			if (colors.indexOf(color) === -1) {
				colors.push(color);
			}

			let author: string = FunctionHelper.applyValue(annotation.markup?.creator, Markup.getCreatorDefault());
			if (authors.indexOf(author) === -1) {
				authors.push(author);
			}

			let type: string = FunctionHelper.applyValue(annotation.subType, Annotation.getSubTypeDefault());
			if (types.indexOf(type) === -1 &&
				Object.values(AnnotationSubType).map((value) => value.toLowerCase()).includes(
					FunctionHelper.applyValue(type.toLowerCase(), Annotation.getSubTypeDefault())
				) && !this.hiddenAnnotations.map((value) => value.toLowerCase()).includes(
					FunctionHelper.applyValue(type.toLowerCase(), Annotation.getSubTypeDefault())
				)
			) {
				// ignore all unknown or hidden annotations
				types.push(type);
			}
		}

		//Add color filter
		if (colors.length > 0) {
			colors.sort(SortHelper.alphanum);

			for (let color of colors) {
				menuItems.push({
					icon: "icon " + (this.state.filterColor.indexOf(color) === -1 ? "icon-check" : "empty"),
					label: FunctionHelper.applyValue(
						color, Translate.value("annotation.filter.color.none", {ns: "sidebar"})
					),
					style: {
						background:
							"linear-gradient(to right, transparent 75%, " +
							FunctionHelper.applyValue(color, "#888 100%, transparent") + " 100%)"
					},
					command: () => {
						let colors: Array<string> = this.state.filterColor;
						let colorIndex: number = colors.indexOf(color);

						if (colorIndex === -1) {
							colors.push(color);
						} else {
							colors.splice(colorIndex, 1);
						}

						this.setState({
							filterColor: colors
						}, this.buildAnnotationList);
					}
				}); // needed to add color data
			}

			menuItems.push({separator: true});
		}

		//Add author filter
		if (authors.length > 0) {
			authors.sort(SortHelper.alphanum);

			for (let author of authors) {
				menuItems.push({
					icon: "icon " + (this.state.filterAuthor.indexOf(author) === -1 ? "icon-check" : "empty"),
					label: FunctionHelper.applyValue(
						author, Translate.value("annotation.filter.author.none", {ns: "sidebar"})
					),
					command: () => {
						let authors: Array<string> = this.state.filterAuthor;
						let authorIndex: number = authors.indexOf(author);

						if (authorIndex === -1) {
							authors.push(author);
						} else {
							authors.splice(authorIndex, 1);
						}

						this.setState({
							filterAuthor: authors
						}, this.buildAnnotationList);
					}
				});
			}

			menuItems.push({separator: true});
		}

		//Add type filter
		if (types.length > 0) {
			types.sort((a: string, b: string) => {
				return SortHelper.alphanum(
					Translate.value("annotation.types." + a.toLowerCase(), {ns: "sidebar"}),
					Translate.value("annotation.types." + b.toLowerCase(), {ns: "sidebar"})
				);
			});

			for (let type of types) {
				menuItems.push({
					icon: "icon " + (this.state.filterType.indexOf(type) === -1 ? "icon-check" : "empty"),
					label: type !== "" ?
						Translate.value("annotation.types." + type.toLowerCase(), {ns: "sidebar"}) :
						Translate.value("annotation.filter.type.unknown", {ns: "sidebar"}),
					command: () => {
						let types: Array<string> = this.state.filterType;
						let typeIndex: number = types.indexOf(type);

						if (typeIndex === -1) {
							types.push(type);
						} else {
							types.splice(typeIndex, 1);
						}

						this.setState({
							filterType: types
						}, this.buildAnnotationList);
					}
				});
			}

			menuItems.push({separator: true});
		}

		let allSelected: boolean = this.state.filterColor.length === 0 && this.state.filterAuthor.length === 0 &&
			this.state.filterType.length === 0
		menuItems.push({
			icon: "icon " + (allSelected ? "icon-check" : "empty"),
			label: Translate.value("annotation.filter.all", {ns: "sidebar"}),
			command: () => {
				this.setState({
					filterAuthor: [],
					filterColor: [],
					filterType: []
				}, this.buildAnnotationList);
			}
		});

		return menuItems;
	}

	private renderAnnotationFunction(): ReactNode {
		let AnnotationFunctions: Array<boolean> = [
			FunctionHelper.applyValue(this.props.showAnnotationReplyFunction, false)
		];

		if (!AnnotationFunctions.includes(true)) {
			return null;
		}

		return (
			<>
				<div className="reply-function sidebar-functions" style={{height: this.state.sidebarFunctionsHeight}}>
					<Resizeable direction={ResizeableDirection.VERTICAL} container={this.body} invert={true}
								resizeCallback={
									(options: { width: string, height: string }) => {
										this.setState({
											sidebarFunctionsHeight: options.height
										});
									}
								}/>
					<SidebarFunctions {...this.props}/>
				</div>
			</>
		);
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);
		let documentSettings: DocumentSettings = portalDocument.getSettings();

		let sortMenu: RefObject<Menu> = createRef<Menu>();
		let filterMenu: RefObject<Menu> = createRef<Menu>();

		let isFiltered: boolean = this.state.filterColor.length > 0 || this.state.filterAuthor.length > 0 ||
			this.state.filterType.length > 0;

		let annotationCount: number = 0;
		for (let annotationCategory of this.state.annotations) {
			annotationCount += FunctionHelper.applyValue(annotationCategory.children, []).length;
		}

		return (
			<>
				<div className="body" style={this.props.visible ? {} : {display: "none"}} ref={this.body}
					 data-help="global.sidebar.comment" data-position="left">
					<div className="toolbar">
						<Button className={(this.state.searchbar ? "active" : "")} icon="icon icon-search"
								tooltip={Translate.value("annotation.search.label", {ns: "sidebar"})}
								onClick={() =>
									this.setState({searchbar: !this.state.searchbar})
								}/>
						<Button icon="icon icon-sort" aria-controls="sortMenu" aria-haspopup={true}
								tooltip={Translate.value("annotation.sort.label", {ns: "sidebar"})}
								onClick={(event) => sortMenu.current!.toggle(event)}/>
						<Menu popup={true} ref={sortMenu} id="sortMenu"
							  appendTo={document.getElementById("root")}
							  model={[
								  {
									  icon: this.getSortOrderIcon(SortType.TYPE),
									  label: Translate.value("annotation.sort.type", {ns: "sidebar"}),
									  command: () => this.toggleSort(SortType.TYPE)
								  },
								  {
									  icon: this.getSortOrderIcon(SortType.DATE),
									  label: Translate.value("annotation.sort.date", {ns: "sidebar"}),
									  command: () => this.toggleSort(SortType.DATE)
								  },
								  {
									  icon: this.getSortOrderIcon(SortType.AUTHOR),
									  label: Translate.value("annotation.sort.author", {ns: "sidebar"}),
									  command: () => this.toggleSort(SortType.AUTHOR)
								  },
								  {
									  icon: this.getSortOrderIcon(SortType.PAGE),
									  label: Translate.value("annotation.sort.page", {ns: "sidebar"}),
									  command: () => this.toggleSort(SortType.PAGE)
								  },
								  {
									  icon: this.getSortOrderIcon(SortType.COLOR),
									  label: Translate.value("annotation.sort.color", {ns: "sidebar"}),
									  command: () => this.toggleSort(SortType.COLOR)
								  }
							  ]}/>
						<Button className={"annotation-filter" + (isFiltered ? " active" : "")}
								icon="icon icon-filter" aria-controls="filterMenu" aria-haspopup={true}
								tooltip={Translate.value("annotation.filter.label", {ns: "sidebar"})}
								onClick={(event) => filterMenu.current!.toggle(event)}/>
						<Menu popup={true} ref={filterMenu} id="filterMenu" model={this.renderFilterMenu()}
							  appendTo={document.getElementById("root")}/>
						<MenuButton {...this.props} icon="icon icon-more"
									menuItems={
										[
											{
												label: Translate.value(
													"annotation.expand", {ns: "sidebar"}
												),
												icon: "icon icon-expand",
												command: this.expandList
											},
											{
												label: Translate.value(
													"annotation.collapse", {ns: "sidebar"}
												),
												icon: "icon icon-collapse",
												command: this.collapseList
											}
										]
									}/>
					</div>
					{this.state.searchbar &&
						<div className="toolbar stretched">
							<div className="p-col">
								<div className="p-inputgroup">
									<Button icon="icon icon-close"
											tooltip={Translate.value(
												"annotation.search.delete", {ns: "sidebar"}
											)}
											onClick={() => {
												if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
													return;
												}

												let portalDocument: PortalDocument =
													this.props.context.documentManager.findDocument(
														this.props.portalDocument!.get().id!
													);
												let documentSettings: DocumentSettings = portalDocument.getSettings();
												documentSettings.annotationSelection = null;

												this.props.context.documentManager.updateGlobalDocumentList(
													{},
													() => this.setState({
														query: ""
													}, this.buildAnnotationList)
												);
											}}/>
									<InputText type="search" value={this.state.query}
											   placeholder={
												   Translate.value(
													   "annotation.search.placeholder", {ns: "sidebar"}
												   )
											   }
											   onChange={(event) => {
												   this.setState({query: (event.target as HTMLInputElement).value});
											   }}/>
									<Button icon="icon icon-check" onClick={this.buildAnnotationList}
											tooltip={Translate.value(
												"annotation.search.confirm", {ns: "sidebar"}
											)}/>
								</div>
							</div>
						</div>
					}
					<div className="annotation-list" ref={this.annotationList}>
						{this.state.annotations.length > 0 ?
							<Tree value={this.state.annotations} selectionMode="single"
								  onExpand={() => {
									  // needs to be set for list expansion to work...
								  }}
								  expandedKeys={this.state.expanded} nodeTemplate={this.renderNode}
								  selectionKeys={documentSettings.annotationSelection} onSelect={this.handleSelect}/> :
							<p className="no-results">
								<Translate {...this.props} data="annotation.empty" namespace="sidebar"/>
							</p>
						}
					</div>
					<div className="toolbar inverted">
						{annotationCount > 0 &&
							<p className="annotation-count">
								<Translate {...this.props} data="annotation.count" namespace="sidebar"
										   values={{count: annotationCount}}/>
							</p>
						}
					</div>
					{this.renderAnnotationFunction()}
				</div>
			</>
		);
	}
}